import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Users, Baby, Globe, MessageCircle, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const EarlyAccessForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    numberOfChildren: '',
    numberOfGrandchildren: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const childrenOptions = [
    { value: '1', label: 'Family of 1' },
    { value: '2', label: 'Family of 2' },
    { value: '3', label: 'Family of 3' },
    { value: '4', label: 'Family of 4' },
    { value: '5', label: 'Family of 5' },
    { value: '6', label: 'Family of 6' },
    { value: '7', label: 'Family of 7' },
    { value: '8+', label: 'Family of 8+' }
  ];

  const grandchildrenOptions = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6+', label: '6+' }
  ];

  const countries = [
    'Australia', 'France', 'United States', 'Canada', 'United Kingdom', 
    'Germany', 'Spain', 'Italy', 'Netherlands', 'Belgium', 'Switzerland',
    'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'New Zealand',
    'Singapore', 'Japan', 'South Korea', 'Brazil', 'Argentina', 'Mexico',
    'Chile', 'Other'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/early-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          country: formData.country,
          number_of_children: formData.numberOfChildren,
          number_of_grandchildren: formData.numberOfGrandchildren,
          message: formData.message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setSubmitStatus('success');
      
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          country: '',
          numberOfChildren: '',
          numberOfGrandchildren: '',
          message: ''
        });
      }, 1000);
      
      setTimeout(() => {
        setSubmitStatus(null);
        onClose();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white/90 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-8 pb-0">
            <button
              onClick={onClose}
              className="absolute top-6 right-6 w-10 h-10 bg-white/60 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center hover:bg-white/80 transition-all duration-300 group"
            >
              <X className="w-5 h-5 text-slate-600 group-hover:text-slate-800" />
            </button>

            <div className="text-center mb-6">
              <motion.div
                className="inline-block mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto">
                  <Star className="w-8 h-8 text-white" />
                </div>
              </motion.div>
              
              <h2 className="text-4xl font-black text-slate-900 mb-2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {t('early_access.title')}
                </span>
              </h2>
              <p className="text-lg text-slate-600">
                {t('early_access.subtitle')}
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 pt-4 space-y-6">
            {/* Name Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-slate-700 font-medium">
                  <Users className="w-4 h-4" />
                  <span>{t('early_access.first_name')}</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300"
                  placeholder={t('early_access.first_name_placeholder')}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-slate-700 font-medium">
                  <Users className="w-4 h-4" />
                  <span>{t('early_access.last_name')}</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300"
                  placeholder={t('early_access.last_name_placeholder')}
                  required
                />
              </div>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-slate-700 font-medium">
                <Globe className="w-4 h-4" />
                <span>{t('early_access.country')}</span>
              </label>
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300"
                required
              >
                <option value="">{t('early_access.select_country')}</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Children and Grandchildren */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-slate-700 font-medium">
                  <Users className="w-4 h-4" />
                  <span>{t('early_access.children_count')}</span>
                </label>
                <select
                  value={formData.numberOfChildren}
                  onChange={(e) => handleInputChange('numberOfChildren', e.target.value)}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300"
                  required
                >
                  <option value="">{t('early_access.select_family_size')}</option>
                  {childrenOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-slate-700 font-medium">
                  <Baby className="w-4 h-4" />
                  <span>{t('early_access.grandchildren_count')}</span>
                </label>
                <select
                  value={formData.numberOfGrandchildren}
                  onChange={(e) => handleInputChange('numberOfGrandchildren', e.target.value)}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300"
                  required
                >
                  <option value="">{t('early_access.select_number')}</option>
                  {grandchildrenOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-slate-700 font-medium">
                <MessageCircle className="w-4 h-4" />
                <span>{t('early_access.message')}</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm rounded-xl border border-white/30 focus:border-blue-400 focus:outline-none transition-all duration-300 resize-none"
                placeholder={t('early_access.message_placeholder')}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('early_access.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('early_access.submit')}</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Status Messages */}
            <AnimatePresence>
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-green-100 border border-green-200 rounded-xl p-4 text-green-800 text-center"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Star className="w-5 h-5" />
                    <span className="font-medium">{t('early_access.success_title')}</span>
                  </div>
                  <p className="text-sm mt-1">{t('early_access.success_message')}</p>
                </motion.div>
              )}
              
              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-100 border border-red-200 rounded-xl p-4 text-red-800 text-center"
                >
                  <p className="font-medium">{t('early_access.error_title')}</p>
                  <p className="text-sm mt-1">{t('early_access.error_message')}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EarlyAccessForm;